/**
 * Universal Robots kinematics implementation
 * Handles forward/inverse kinematics calculations for UR robots
 */

import { inv, multiply, subtract } from 'mathjs';
import { Matrix3, Matrix4, Vector3, Vector4 } from 'three';
import { QNear } from '../../../util/machine-variable.utils';
import { JointDHParameters, Pose, RobotKinematics } from '../../robot.types';
import { URModel } from './ur.types';

export class URKinematics implements RobotKinematics {
  constructor(private model: URModel) {}

  getForwardKinematics(jointPositions: QNear, tcp: Pose): Pose {
    const DHParams = this.getDHParameters();

    // Initialize transformation matrix T_0^6 to the identity matrix
    let transformationMatrix = new Matrix4().identity();

    // Compute transformation matrix T_0^6 by multiplying individual matrices
    for (let i = 0; i < DHParams.length; i++) {
      const { theta, a, d, alpha } = DHParams[i];
      const angle = jointPositions ? jointPositions[i] : theta;
      const t_i_iplus1 = this.computeTransformationMatrix(angle, a, d, alpha);
      transformationMatrix = transformationMatrix.multiply(t_i_iplus1);
    }

    const axisAngle = this.getAxisAngleFromRotationMatrix(transformationMatrix);
    let pose_endEffector: Pose = [
      transformationMatrix.elements[12],
      transformationMatrix.elements[13],
      transformationMatrix.elements[14],
      axisAngle.x,
      axisAngle.y,
      axisAngle.z,
    ];

    if (tcp) {
      pose_endEffector = this.pose_trans(pose_endEffector, tcp);
    }

    return pose_endEffector;
  }

  getInverseKinematics(desiredPose: Pose, tcpOffset: Pose): Array<QNear> {
    // Define constants
    const anglePossibilities: number[][] = Array.from({ length: 6 }, () =>
      Array(8).fill(0)
    );
    const identityVector: number[] = [0, 0, 0, 1];

    // Fetch DH params based on robot
    const DHParams = this.getDHParameters();
    const aValues: number[] = DHParams.map(({ a }) => a);
    const dValues: number[] = DHParams.map(({ d }) => d);

    // Extract pose at tool flange
    const poseAtToolFlange = this.get_tool_flange_pose(desiredPose, tcpOffset);
    // Get T_matrix and its transpose
    const T_matrix_tranpose =
      this.getTransformMatrix(poseAtToolFlange).transpose();

    // Convert 4x4 matrix to number array
    const T_matrix: number[][] = [
      T_matrix_tranpose.elements.slice(0, 4),
      T_matrix_tranpose.elements.slice(4, 8),
      T_matrix_tranpose.elements.slice(8, 12),
      T_matrix_tranpose.elements.slice(12, 16),
    ];

    // Calculate initial position vector relative to base frame
    const P_05 = subtract(
      multiply(T_matrix, [0, 0, -dValues[5], 1]),
      identityVector
    );

    // ******* THETA_1: BASE JOINT *******
    const psi = Math.atan2(P_05[1], P_05[0]);
    const phi = Math.acos(
      dValues[3] / Math.sqrt(P_05[1] * P_05[1] + P_05[0] * P_05[0])
    );
    for (let i = 0; i < 8; i++) {
      anglePossibilities[0][i] =
        i < 4 ? Math.PI / 2 + psi + phi : Math.PI / 2 + psi - phi;
    }

    // ****** THETA_5: WRIST 2 ********
    let jointValues: number[] = [0, 4];
    for (let i = 0; i < jointValues.length; i++) {
      const c = jointValues[i];
      const T_16 = this.transformJointSpaceToTool(
        T_matrix,
        1,
        anglePossibilities,
        c
      );
      const acosValue = (T_16[2][3] - dValues[3]) / dValues[5];
      const acosValuePositive = Math.acos(acosValue);
      const acosValueNegative = -acosValuePositive;

      anglePossibilities[4][c] = acosValuePositive;
      anglePossibilities[4][c + 1] = acosValuePositive;
      anglePossibilities[4][c + 2] = acosValueNegative;
      anglePossibilities[4][c + 3] = acosValueNegative;
    }

    // ****** THETA_6: WRIST 3 ********
    jointValues = [0, 2, 4, 6];
    for (let i = 0; i < jointValues.length; i++) {
      const c = jointValues[i];
      const T_16 = this.transformJointSpaceToTool(
        T_matrix,
        1,
        anglePossibilities,
        c
      );
      const T_16_inv = inv(T_16);
      const atan2Value = Math.atan2(
        -T_16_inv[1][2] / Math.sin(anglePossibilities[4][c]),
        T_16_inv[0][2] / Math.sin(anglePossibilities[4][c])
      );
      anglePossibilities[5][c] = atan2Value;
      anglePossibilities[5][c + 1] = atan2Value;
    }

    // ********* THETA_3: ELBOW ********
    jointValues = [0, 2, 4, 6];
    for (let i = 0; i < jointValues.length; i++) {
      const c = jointValues[i];
      const T_16 = this.transformJointSpaceToTool(
        T_matrix,
        1,
        anglePossibilities,
        c
      );
      const T_65 = this.forward_kin_T_matrix(6, anglePossibilities, c);
      const T_54 = this.forward_kin_T_matrix(5, anglePossibilities, c);
      const T_65_T_54 = inv(multiply(T_54, T_65));
      const T_14 = multiply(T_16, T_65_T_54);
      const P_13 = subtract(
        multiply(T_14, [0, -dValues[3], 0, 1]),
        identityVector
      );
      const t3 = Math.acos(
        (this.euclideanNorm(P_13) ** 2 - aValues[1] ** 2 - aValues[2] ** 2) /
          (2 * aValues[1] * aValues[2])
      );
      anglePossibilities[2][c] = t3;
      anglePossibilities[2][c + 1] = t3;
    }

    // ****** THETA_2: SHOULDER and THETA_4: WRIST 1 *******
    jointValues = [0, 1, 2, 3, 4, 5, 6, 7];
    for (let i = 0; i < jointValues.length; i++) {
      const c = jointValues[i];
      const T_16 = this.transformJointSpaceToTool(
        T_matrix,
        1,
        anglePossibilities,
        c
      );
      const T_65 = this.forward_kin_T_matrix(6, anglePossibilities, c);
      const T_54 = this.forward_kin_T_matrix(5, anglePossibilities, c);
      const T_65_inv = inv(T_65);
      const T_54_inv = inv(T_54);
      const T_65_x_T_54 = multiply(T_65_inv, T_54_inv);
      const T_14 = multiply(T_16, T_65_x_T_54);
      const P_13 = subtract(
        multiply(T_14, [0, -dValues[3], 0, 1]),
        identityVector
      );
      // theta_2
      anglePossibilities[1][c] =
        -Math.atan2(P_13[1], -P_13[0]) +
        Math.asin(
          aValues[2] *
            Math.sin(anglePossibilities[2][c] / this.euclideanNorm(P_13))
        );
      // theta_4
      const T_32 = this.forward_kin_T_matrix(3, anglePossibilities, c);
      const T_21 = this.forward_kin_T_matrix(2, anglePossibilities, c);
      const T_34 = multiply(inv(T_32), multiply(inv(T_21), T_14));
      anglePossibilities[3][c] = Math.atan2(T_34[1][0], T_34[0][0]);
    }

    // Extract J_angle arrays
    const J_angles: Array<QNear> = [];
    for (let i = 0; i < 8; i++) {
      J_angles.push([
        anglePossibilities[0][i],
        anglePossibilities[1][i],
        anglePossibilities[2][i],
        anglePossibilities[3][i],
        anglePossibilities[4][i],
        anglePossibilities[5][i],
      ]);
    }
    return J_angles;
  }

  isWaypointReachable(pose: Pose, tcpOffset: Pose): boolean {
    const Joint_angles_list = this.getInverseKinematics(pose, tcpOffset);
    for (const near of Joint_angles_list) {
      if (!near.some((value) => value === 0 || isNaN(value))) {
        return true;
      }
    }
    return false;
  }

  getDHParameters(): Array<JointDHParameters> {
    let dhParams: Array<JointDHParameters> = [];
    switch (this.model) {
      case URModel.UR10:
        dhParams = [
          { theta: 0, a: 0, d: 0.1807, alpha: Math.PI / 2 },
          { theta: 0, a: -0.6127, d: 0, alpha: 0 },
          { theta: 0, a: -0.57155, d: 0, alpha: 0 },
          { theta: 0, a: 0, d: 0.17415, alpha: Math.PI / 2 },
          { theta: 0, a: 0, d: 0.11985, alpha: -Math.PI / 2 },
          { theta: 0, a: 0, d: 0.11655, alpha: 0 },
        ];
        break;

      case URModel.UR20:
        dhParams = [
          { theta: 0, a: 0, d: 0.2363, alpha: Math.PI / 2 },
          { theta: 0, a: -0.862, d: 0, alpha: 0 },
          { theta: 0, a: -0.7287, d: 0, alpha: 0 },
          { theta: 0, a: 0, d: 0.201, alpha: Math.PI / 2 },
          { theta: 0, a: 0, d: 0.1593, alpha: -Math.PI / 2 },
          { theta: 0, a: 0, d: 0.1543, alpha: 0 },
        ];
        break;

      case URModel.UR8_LONG:
        throw new Error(`${this.model} not implemented`);

      default:
        // Handle unknown robot models - use UR10 as fallback
        dhParams = [
          { theta: 0, a: 0, d: 0.1807, alpha: Math.PI / 2 },
          { theta: 0, a: -0.6127, d: 0, alpha: 0 },
          { theta: 0, a: -0.57155, d: 0, alpha: 0 },
          { theta: 0, a: 0, d: 0.17415, alpha: Math.PI / 2 },
          { theta: 0, a: 0, d: 0.11985, alpha: -Math.PI / 2 },
          { theta: 0, a: 0, d: 0.11655, alpha: 0 },
        ];
        break;
    }
    return dhParams;
  }

  // Helper methods (extracted from PoseUtil)
  private pose_trans(p1: Pose, p2: Pose): Pose {
    const transform1 = this.getTransformMatrix(p1);
    const transform2 = this.getTransformMatrix(p2);

    const result = transform1.multiply(transform2);
    const axisAngle = this.getAxisAngleFromRotationMatrix(result);

    return [
      result.elements[12],
      result.elements[13],
      result.elements[14],
      axisAngle.x,
      axisAngle.y,
      axisAngle.z,
    ];
  }

  private pose_inv(p: Pose): Pose {
    const rotationMatrix = this.getRotationMatrix(p.slice(3));

    const invertedTransformMatrix = this.getTransformMatrix(
      p,
      rotationMatrix
    ).invert();

    const axisAngle = this.getAxisAngleFromRotationMatrix(
      rotationMatrix.invert()
    );

    return [
      invertedTransformMatrix.elements[12],
      invertedTransformMatrix.elements[13],
      invertedTransformMatrix.elements[14],
      axisAngle.x,
      axisAngle.y,
      axisAngle.z,
    ];
  }

  private get_tool_flange_pose(actualPose: Pose, tcpOffset: Pose): Pose {
    const toolFlangePose = this.pose_trans(
      actualPose,
      this.pose_inv(tcpOffset)
    );
    return toolFlangePose;
  }

  private getTransformMatrix(p: Pose, rotationMatrixToUse?: Matrix4): Matrix4 {
    const translation = p.slice(0, 3);

    let rotationMatrix: Matrix3;

    if (rotationMatrixToUse) {
      rotationMatrix = new Matrix3().getNormalMatrix(rotationMatrixToUse);
    } else {
      rotationMatrix = new Matrix3().getNormalMatrix(
        this.getRotationMatrix(p.slice(3))
      );
    }

    const { m00, m01, m02, m10, m11, m12, m20, m21, m22 } =
      this.extractPartsFromMatrix(rotationMatrix);

    const matrix = new Matrix4();

    matrix.set(
      m00,
      m01,
      m02,
      translation[0],
      m10,
      m11,
      m12,
      translation[1],
      m20,
      m21,
      m22,
      translation[2],
      0,
      0,
      0,
      1
    );

    return matrix;
  }

  private extractPartsFromMatrix(matrix: Matrix3) {
    return {
      m00: matrix.elements[0],
      m01: matrix.elements[3],
      m02: matrix.elements[6],
      m10: matrix.elements[1],
      m11: matrix.elements[4],
      m12: matrix.elements[7],
      m20: matrix.elements[2],
      m21: matrix.elements[5],
      m22: matrix.elements[8],
    };
  }

  private getRotationMatrix(rotation: number[]): Matrix4 {
    const vector = this.getAxisAngleFromPoseRotation(rotation);

    return new Matrix4().makeRotationAxis(
      new Vector3(vector.x, vector.y, vector.z),
      vector.w
    );
  }

  private getAxisAngleFromPoseRotation(rotation: number[]): {
    x: number;
    y: number;
    z: number;
    w: number;
  } {
    const vector = new Vector3(rotation[0], rotation[1], rotation[2]);
    const angle = vector.length();

    let x = 0;
    let y = 0;
    let z = 1;

    if (angle === 0) {
      x = 1;
      y = 0;
      z = 0;
    } else {
      x = vector.x / angle;
      y = vector.y / angle;
      z = vector.z / angle;
    }

    return { x, y, z, w: angle };
  }

  private getAxisAngleFromRotationMatrix(rotationMatrix: Matrix4): Vector3 {
    const axisAngle = new Vector4().setAxisAngleFromRotationMatrix(
      rotationMatrix
    );

    const vector = new Vector3();

    vector.setX(axisAngle.x * axisAngle.w);
    vector.setY(axisAngle.y * axisAngle.w);
    vector.setZ(axisAngle.z * axisAngle.w);

    return vector;
  }

  private computeTransformationMatrix(
    theta: number,
    a: number,
    d: number,
    alpha: number
  ): Matrix4 {
    const cosTheta = Math.cos(theta);
    const sinTheta = Math.sin(theta);
    const cosAlpha = Math.cos(alpha);
    const sinAlpha = Math.sin(alpha);

    return new Matrix4(
      cosTheta,
      -sinTheta * cosAlpha,
      sinTheta * sinAlpha,
      a * cosTheta,
      sinTheta,
      cosTheta * cosAlpha,
      -cosTheta * sinAlpha,
      a * sinTheta,
      0,
      sinAlpha,
      cosAlpha,
      d,
      0,
      0,
      0,
      1
    );
  }

  private transformJointSpaceToTool(
    toolMatrix: number[][],
    joint: number,
    joint_angles: number[][],
    columnVal: number
  ): number[][] {
    const T_1_0 = this.forward_kin_T_matrix(joint, joint_angles, columnVal);
    const T_1_0_inverse = inv(T_1_0);
    const T_16 = multiply(T_1_0_inverse, toolMatrix);
    return T_16;
  }

  private forward_kin_T_matrix(
    rowValue: number,
    inputTheta: number[][],
    columnVal: number
  ): number[][] {
    const DHParams = this.getDHParameters();
    const aValues: number[] = DHParams.map(({ a }) => a);
    const dValues: number[] = DHParams.map(({ d }) => d);
    const alphaValues: number[] = DHParams.map(({ alpha }) => alpha);

    const T_angleBtwJoints = new Matrix4().identity();
    T_angleBtwJoints.setPosition(new Vector3(aValues[rowValue - 1], 0, 0));

    const T_distance = new Matrix4().identity();
    T_distance.setPosition(new Vector3(0, 0, dValues[rowValue - 1]));

    const Rzt = new Matrix4().set(
      Math.cos(inputTheta[rowValue - 1][columnVal]),
      -Math.sin(inputTheta[rowValue - 1][columnVal]),
      0,
      0,
      Math.sin(inputTheta[rowValue - 1][columnVal]),
      Math.cos(inputTheta[rowValue - 1][columnVal]),
      0,
      0,
      0,
      0,
      1,
      0,
      0,
      0,
      0,
      1
    );

    const Rxa = new Matrix4().set(
      1,
      0,
      0,
      0,
      0,
      Math.cos(alphaValues[rowValue - 1]),
      -Math.sin(alphaValues[rowValue - 1]),
      0,
      0,
      Math.sin(alphaValues[rowValue - 1]),
      Math.cos(alphaValues[rowValue - 1]),
      0,
      0,
      0,
      0,
      1
    );

    const A_i = T_distance.multiply(Rzt)
      .multiply(T_angleBtwJoints)
      .multiply(Rxa);

    const resultArray: number[][] = [];
    for (let i = 0; i < 4; i++) {
      resultArray.push([
        A_i.elements[i],
        A_i.elements[i + 4],
        A_i.elements[i + 8],
        A_i.elements[i + 12],
      ]);
    }

    return resultArray;
  }

  private euclideanNorm(vector: number[]): number {
    let sumOfSquares = 0;
    for (const element of vector) {
      sumOfSquares += element ** 2;
    }
    return Math.sqrt(sumOfSquares);
  }
}
